import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import type { ContentDetailsTestItem } from '~/models/Report'
import { faCircleCheck as faCircleCheckSolid, faCircleXmark as faCircleXmarkSolid } from '@fortawesome/pro-solid-svg-icons'
import { faCircle } from '@fortawesome/pro-solid-svg-icons'
import { faCircleExclamation } from '@fortawesome/duotone-regular-svg-icons'

export interface TaskIconResult {
  icon: IconDefinition
  class: string
  title: string
}

type TranslateFunction = (key: string, params?: Record<string, string | number>) => string

export function getTaskStatusIcon(
  result: string | null,
  t: TranslateFunction,
  score?: number,
  totalScore?: number
): TaskIconResult {
  if (!result || (score !== undefined && totalScore !== undefined && (score < 0 || totalScore <= 0 || score > totalScore))) {
    return {
      icon: faCircle,
      class: 'text-gray-30',
      title: t('reports.status.noData')
    }
  }

  if (result === 'CORRECT') {
    const scoreText = score !== undefined && totalScore !== undefined ? `${score}/${totalScore} - ` : ''
    return {
      icon: faCircleCheckSolid,
      class: 'text-green-40',
      title: `${scoreText}${t('reports.status.passed')}`
    }
  }

  if (result === 'PARTIALLY_CORRECT') {
    const scoreText = score !== undefined && totalScore !== undefined ? `${score}/${totalScore} - ` : ''
    return {
      icon: faCircleExclamation,
      class: '[--fa-primary-color:theme(colors.orange.40)] [--fa-secondary-color:theme(colors.orange.10)]',
      title: `${scoreText}${t('reports.status.partiallyPassed')}`
    }
  }

  if (result === 'INCORRECT') {
    const scoreText = score !== undefined && totalScore !== undefined ? `${score}/${totalScore} - ` : ''
    return {
      icon: faCircleXmarkSolid,
      class: 'text-coral-40',
      title: `${scoreText}${t('reports.status.failed')}`
    }
  }

  if (score !== undefined && totalScore !== undefined) {
    if (score === totalScore) {
      return {
        icon: faCircleCheckSolid,
        class: 'text-green-40',
        title: `${score}/${totalScore} - ${t('reports.status.passed')}`
      }
    } else if (score > 0) {
      return {
        icon: faCircleExclamation,
        class: '[--fa-primary-color:theme(colors.orange.40)] [--fa-secondary-color:theme(colors.orange.10)]',
        title: `${score}/${totalScore} - ${t('reports.status.partiallyPassed')}`
      }
    } else {
      return {
        icon: faCircleXmarkSolid,
        class: 'text-coral-40',
        title: `${score}/${totalScore} - ${t('reports.status.failed')}`
      }
    }
  }

  return {
    icon: faCircle,
    class: 'text-gray-30',
    title: t('reports.status.unknown')
  }
}

export function getTaskItemIcon(item: ContentDetailsTestItem | null, t: TranslateFunction): TaskIconResult {
  if (!item) {
    return getTaskStatusIcon(null, t)
  }

  return getTaskStatusIcon(item.result, t, item.score, item.targetScore)
}
